import { FC } from "react";

import { Box, Grid } from "@mui/material";
import { DiscountType, DiscountScope, getDefaultDiscountScope } from "@simplyk/common";
import { useForm, useFieldArray } from "react-hook-form";
import { v4 } from "uuid";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "../FormStepContainer";
import { shouldShowSkipStep } from "../helpers/shouldShowSkipStep";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { mapDataToCreateDiscountCodes, mapDataToProvideDiscountCodes } from "./discountCode.helper";
import { DiscountCodeData } from "./discountCode.types";
import { DiscountCodeRow } from "./DiscountCodeRow";

import { isMultidateTicketing } from "@/common/helpers/ticketing";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DiscountOutput } from "@/common/types/trpc";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { Add } from "@/icons/outlined";

export type StepDiscountCodeFormData = {
  discountCodes: DiscountCodeData[];
  discountsToDelete: string[];
};

export const TicketingLiveEditorStepDiscountCode: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const defaultValues: StepDiscountCodeFormData = {
    discountCodes: mapDataToProvideDiscountCodes(apiTicketing.discounts, apiTicketing.rates, apiTicketing.locale),
    discountsToDelete: [],
  };
  const form = useForm<StepDiscountCodeFormData>({ defaultValues });

  const updateTicketingDiscounts = trpc.updateTicketingDiscounts.useMutation();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepDiscountCodeFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      return await updateTicketingDiscounts.mutateAsync({
        ticketingId: apiTicketing.id,
        discounts: mapDataToCreateDiscountCodes(formData.discountCodes),
        discountsToDelete: formData.discountsToDelete,
      });
    },
  });

  const {
    fields: discountCodes,
    append: addDiscountCode,
    remove: deleteDiscountCode,
  } = useFieldArray({
    control: form.control,
    name: "discountCodes",
    keyName: "_id",
  });

  const discountsToDelete = form.watch("discountsToDelete") || [];

  const handleAddDiscountCode = () => {
    logUserActionEvent(AmplitudeEvents.FormDiscountCodeAdded);

    const isFirstDiscountCode = discountCodes.length === 0;

    addDiscountCode({
      id: v4(),
      body: isFirstDiscountCode
        ? t("dashboard", "ticketing.defaultDiscountBody", {
            currentYear: new Date().getFullYear().toString(),
          })
        : "",
      amount: isFirstDiscountCode ? 10 : 0,
      unit: DiscountType.Dollars,
      limit: null,
      selectedRates: [],
      scope: getDefaultDiscountScope(apiTicketing.rates?.length || 0),
    });
  };

  const handleDeleteDiscountCode = (index: number) => {
    const idToDelete = discountCodes[index].id;
    deleteDiscountCode(index);

    if (idToDelete) {
      form.setValue("discountsToDelete", [...discountsToDelete, idToDelete], {
        shouldDirty: true,
      });
    }
  };

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const discountCodeInPreview = [dummyDiscountCode]; // We do not care of the data of the discounts, we just want to display the field in the preview
      liveDataToMutate.ticketing!.discounts = discountCodeInPreview;
      liveDataToMutate.placeholders.isDiscountCode = !formData.discountCodes.length;
    },
  });

  return (
    <FormStepContainer
      title={t("dashboard", "ticketing.discountCodes")}
      description={t("dashboard", "ticketing.discountCodesDescription")}
      form={form}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
        nextButtonLabel: shouldShowSkipStep(form) ? "skip" : "next",
      }}
    >
      <Grid container spacing={2}>
        {discountCodes.map((discountCode, index) => (
          <DiscountCodeRow
            key={discountCode.id}
            index={index}
            onDelete={() => handleDeleteDiscountCode(index)}
            form={form}
            discountCode={discountCode}
            hideDeleteButton={false}
            ticketingHasMultipleDates={isMultidateTicketing(
              apiTicketing.formCategory,
              apiTicketing.occurrences?.length ?? 0
            )}
            checkForDuplicate={(couponBody: string) =>
              discountCodes.some(
                (dc) => dc.id !== discountCode.id && dc.body.toLowerCase() === couponBody.toLowerCase()
              )
            }
            availableRates={apiTicketing.rates || []}
            formCategory={apiTicketing.formCategory}
          />
        ))}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-start">
            <DashboardButton
              variant="outlined"
              vibe="brand"
              startIcon={<Add />}
              onClick={handleAddDiscountCode}
              data-test="discount-code-add"
            >
              {t("dashboard", discountCodes.length ? "ticketing.addAnotherDiscountCode" : "ticketing.addADiscountCode")}
            </DashboardButton>
          </Box>
        </Grid>
      </Grid>
    </FormStepContainer>
  );
};

const dummyDiscountCode: DiscountOutput & { scope?: DiscountScope } = {
  amount: 100,
  body: "123",
  id: "123",
  limit: 100,
  remainingUses: 100,
  sortIndex: 1,
  ticketingId: "123",
  type: DiscountType.Dollars,
  scope: DiscountScope.PerTicketAllRates,
};
